import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, MapPin, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import MemberAgreementFlow from '../components/MemberAgreementFlow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MembershipPage() {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [prepaidProducts, setPrepaidProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPrepaid, setSelectedPrepaid] = useState(null);
  const [prepaidForm, setPrepaidForm] = useState({
    first_name: '', last_name: '', email: '', date_of_birth: '', home_site_id: '',
  });
  const [prepaidLoading, setPrepaidLoading] = useState(false);
  // step: 1=Gym, 2=Plan, 3=Details, 4=Payment, 5=Agreement
  const [step, setStep] = useState(1);
  const [proRata, setProRata] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '',
    home_site_id: '', date_of_birth: '',
    address_line1: '', address_line2: '', city: '', postcode: '',
    emergency_contact_name: '', emergency_contact_phone: '',
    marketing_opt_in: false,
    account_holder_name: '', account_number: '', sort_code: '', bank_name: '',
    business_name: '', self_employed_utr: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [memberId, setMemberId] = useState(null);
  const navigate = useNavigate();

  const isPTPlan = selectedPlan && (selectedPlan.plan_type === 'pt_monthly' || selectedPlan.plan_type === 'pt_adhoc');
  const isAdHocPT = selectedPlan?.plan_type === 'pt_adhoc';

  // Selected site details (resolved from locations)
  const selectedSite = locations.find(l => l.id === formData.home_site_id) || null;

  // Load sites + prepaid products once on mount (prepaid is site-agnostic)
  useEffect(() => {
    axios.get(`${API}/sites`).then(res => {
      const items = res.data.items || res.data || [];
      // Only public gyms — exclude warehouse / private sites
      setLocations(items.filter(s => s.is_public !== false && s.site_type !== 'warehouse'));
    }).catch(() => {});
    axios.get(`${API}/prepaid/products`).then(res => {
      setPrepaidProducts(res.data.items || []);
    }).catch(() => {});
  }, []);

  // When user picks a gym, sync across formData + prepaidForm and refetch site-specific plans
  useEffect(() => {
    if (!formData.home_site_id) { setPlans([]); return; }
    setPrepaidForm(p => ({ ...p, home_site_id: formData.home_site_id }));
    setPlansLoading(true);
    axios.get(`${API}/plans?is_public=true&site_id=${formData.home_site_id}`)
      .then(res => setPlans(res.data.items || []))
      .catch(() => {
        // Fallback to base pricing only if site-specific call fails
        axios.get(`${API}/plans?is_public=true`).then(res => setPlans(res.data.items || [])).catch(() => setPlans([]));
      })
      .finally(() => setPlansLoading(false));
  }, [formData.home_site_id]);

  const startPrepaidCheckout = async () => {
    if (!selectedPrepaid) return;
    const errors = {};
    if (!prepaidForm.first_name.trim()) errors.first_name = 'First name is required';
    if (!prepaidForm.last_name.trim()) errors.last_name = 'Last name is required';
    if (!prepaidForm.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(prepaidForm.email)) errors.email = 'Valid email is required';
    if (!prepaidForm.home_site_id) errors.home_site_id = 'Select your home gym';
    if (!prepaidForm.date_of_birth) errors.date_of_birth = 'Date of birth required';
    if (Object.keys(errors).length) { setFormErrors(errors); return; }
    setPrepaidLoading(true);
    try {
      const res = await axios.post(`${API}/prepaid/checkout`, {
        product_id: selectedPrepaid.id,
        ...prepaidForm,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Checkout failed');
      setPrepaidLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPlan?.id && formData.home_site_id) {
      axios.get(`${API}/plans/pro-rata?plan_id=${selectedPlan.id}&site_id=${formData.home_site_id}`)
        .then(res => setProRata(res.data)).catch(() => setProRata(null));
    } else if (selectedPlan?.id) {
      axios.get(`${API}/plans/pro-rata?plan_id=${selectedPlan.id}`)
        .then(res => setProRata(res.data)).catch(() => setProRata(null));
    }
  }, [selectedPlan?.id, formData.home_site_id]);

  const [formErrors, setFormErrors] = useState({});

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const errors = {};
    if (!formData.first_name.trim()) errors.first_name = 'First name is required';
    if (!formData.last_name.trim()) errors.last_name = 'Last name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Please enter a valid email address';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    if (!formData.date_of_birth) errors.date_of_birth = 'Date of birth is required';
    if (!formData.address_line1.trim()) errors.address_line1 = 'Address is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.postcode.trim()) errors.postcode = 'Postcode is required';
    if (!formData.home_site_id) errors.home_site_id = 'Please select your home gym';
    if (!formData.password || formData.password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (formData.date_of_birth) {
      const dob = new Date(formData.date_of_birth);
      const today = new Date();
      if (dob > today) errors.date_of_birth = 'Date of birth cannot be in the future';
      const age = today.getFullYear() - dob.getFullYear() - ((today.getMonth() < dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) ? 1 : 0);
      if (age < 16) errors.date_of_birth = 'You must be at least 16 years old';
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) { setError('Please correct the highlighted fields below.'); return; }
    // Check for duplicate email before proceeding
    try {
      const check = await axios.post(`${API}/auth/check-email`, { email: formData.email });
      if (check.data?.exists) { setFormErrors({ email: 'This email is already registered. Please use a different email or log in.' }); setError('This email address is already registered.'); return; }
    } catch (err) {
      if (err.response?.status === 409) { setFormErrors({ email: 'This email is already registered.' }); setError('This email address is already registered.'); return; }
    }
    setStep(4);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isAdHocPT) {
      if (!formData.account_holder_name || !formData.account_number || !formData.sort_code) { setError('Please complete all bank details for Direct Debit setup.'); return; }
      if (formData.account_number.replace(/\s/g, '').length !== 8) { setError('Account number must be 8 digits.'); return; }
      if (formData.sort_code.replace(/[-\s]/g, '').length !== 6) { setError('Sort code must be 6 digits.'); return; }
    }
    setLoading(true);
    try {
      const payload = {
        email: formData.email, password: formData.password,
        first_name: formData.first_name, last_name: formData.last_name,
        phone: formData.phone, date_of_birth: formData.date_of_birth,
        address_line1: formData.address_line1, address_line2: formData.address_line2 || null,
        city: formData.city, postcode: formData.postcode,
        home_site_id: formData.home_site_id || null,
        plan_id: selectedPlan?.id || null,
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        marketing_opt_in: formData.marketing_opt_in,
        account_holder_name: formData.account_holder_name || null,
        account_number: formData.account_number?.replace(/\s/g, '') || null,
        sort_code: formData.sort_code?.replace(/[-\s]/g, '') || null,
        bank_name: formData.bank_name || null,
        business_name: formData.business_name || null,
        self_employed_utr: formData.self_employed_utr || null,
      };
      const response = await axios.post(`${API}/auth/register`, payload);
      if (response.data.access_token) {
        localStorage.setItem('uf_token', response.data.access_token);
        setMemberId(response.data.member_id || response.data.user_id || response.data.id);
        if (response.data.account_type === 'pt') {
          setSuccess(true);
          setTimeout(() => navigate('/pt/hub'), 2000);
        } else {
          setStep(5);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    }
    setLoading(false);
  };

  const onAgreementComplete = () => {
    setSuccess(true);
    setTimeout(() => navigate('/hub'), 2000);
  };

  const memberPlans = plans.filter(p => p.plan_type === 'member' || p.plan_type === 'prepaid' || p.plan_type === 'day_pass');
  const ptPlans = plans.filter(p => p.plan_type === 'pt_monthly' || p.plan_type === 'pt_adhoc');
  const stepLabels = [
    'Choose Gym',
    'Choose Plan',
    'Your Details',
    isAdHocPT ? 'Card Setup' : 'Direct Debit',
    'Agreement',
  ];

  // City helper — strip the "UltraFlex " prefix from the site name for a cleaner card title
  const cityOf = (site) => (site?.name || '').replace(/^UltraFlex\s+/i, '') || site?.name || '';

  if (success) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center" data-testid="signup-success">
        <div className="text-center">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"><Check size={40} className="text-white" /></div>
          <h1 className="font-heading text-4xl font-bold text-white mb-4">{isPTPlan ? 'Welcome to UltraFlex PT!' : 'Welcome to UltraFlex!'}</h1>
          <p className="text-uf-textMuted mb-4">{isPTPlan ? "Your PT application has been submitted. We'll review and activate your account shortly." : 'Your membership has been created successfully.'}</p>
          <p className="text-uf-textMuted text-sm">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="membership-page">
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-uf-red font-heading text-xl tracking-[0.3em] mb-2">JOIN US</p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white">Membership Plans</h1>
            <p className="text-uf-textMuted text-lg mt-4">Choose your gym, then pick your plan — pricing varies by location.</p>
          </motion.div>

          {/* Stepper */}
          <div className="flex justify-center items-center gap-4 mb-12 flex-wrap">
            {stepLabels.map((label, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="w-12 h-px bg-uf-border"></div>}
                <div className={`flex items-center gap-2 ${step >= i + 1 ? 'text-uf-red' : 'text-uf-textMuted'}`}>
                  <span className={`w-8 h-8 flex items-center justify-center border text-sm ${step >= i + 1 ? 'border-uf-red bg-uf-red text-white' : 'border-uf-border'}`}>{i + 1}</span>
                  <span className="hidden sm:inline font-heading uppercase tracking-wider text-sm">{label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* STEP 1 — Choose Gym */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="step-gym-picker">
              <div className="text-center mb-10">
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">Select Your Home Gym</h2>
                <p className="text-uf-textMuted max-w-2xl mx-auto">Membership pricing varies between sites. Pick your home gym first and we'll show you the accurate plans for that location.</p>
              </div>
              {locations.length === 0 ? (
                <div className="text-center text-uf-textMuted py-12">Loading gyms…</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
                  {locations.map((loc, idx) => {
                    const isPicked = formData.home_site_id === loc.id;
                    return (
                      <motion.button
                        key={loc.id}
                        type="button"
                        onClick={() => setFormData(f => ({ ...f, home_site_id: loc.id }))}
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
                        className={`group text-left bg-uf-surface border-2 p-6 transition-colors ${isPicked ? 'border-uf-red' : 'border-uf-border hover:border-uf-red/60'}`}
                        data-testid={`gym-card-${loc.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <MapPin size={20} className={isPicked ? 'text-uf-red' : 'text-uf-textMuted'} />
                          {isPicked && (
                            <span className="inline-flex items-center gap-1 text-xs bg-uf-red text-white px-2 py-1 font-heading uppercase tracking-wider">
                              <Check size={12} /> Selected
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading text-2xl font-bold text-white mb-1">{cityOf(loc)}</h3>
                        <p className="text-uf-textMuted text-xs mb-3">{loc.name}</p>
                        {loc.address && <p className="text-uf-textMuted text-xs leading-relaxed mb-4 line-clamp-2">{loc.address}</p>}
                        <div className={`text-xs font-heading uppercase tracking-wider ${isPicked ? 'text-uf-red' : 'text-uf-textMuted group-hover:text-white'}`}>
                          {isPicked ? 'Gym Selected →' : 'Choose this gym →'}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              )}
              {formData.home_site_id && (
                <motion.div className="text-center mt-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <button
                    onClick={() => setStep(2)}
                    className="btn-primary"
                    data-testid="continue-to-plans-btn"
                  >
                    <span>Continue to {cityOf(selectedSite)} Plans →</span>
                  </button>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* STEP 2 — Choose Plan (site-specific pricing) */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="step-plan-picker">
              <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                <button
                  onClick={() => { setStep(1); setSelectedPlan(null); setSelectedPrepaid(null); }}
                  className="text-uf-textMuted hover:text-white flex items-center gap-2"
                  data-testid="back-to-gym-btn"
                >
                  <ArrowLeft size={16} /> Change gym
                </button>
                {selectedSite && (
                  <div className="flex items-center gap-2 bg-uf-surface border border-uf-border px-4 py-2" data-testid="selected-gym-badge">
                    <MapPin size={16} className="text-uf-red" />
                    <span className="text-white font-heading uppercase tracking-wider text-sm">{cityOf(selectedSite)}</span>
                  </div>
                )}
              </div>

              {plansLoading && <div className="text-center text-uf-textMuted py-12">Loading plans for {cityOf(selectedSite)}…</div>}

              {!plansLoading && prepaidProducts.length > 0 && (
                <div className="mb-12">
                  <h2 className="font-heading text-2xl font-bold text-white mb-2 text-center">Prepaid Memberships</h2>
                  <p className="text-uf-textMuted text-center mb-6 text-sm">Pay in full · no Direct Debit · fixed-term access from the day you join</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                    {prepaidProducts.map((pp, index) => (
                      <motion.div key={pp.id} className={`tier-card cursor-pointer flex flex-col ${selectedPrepaid?.id === pp.id ? 'border-uf-red' : ''}`}
                        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}
                        onClick={() => { setSelectedPrepaid(pp); setSelectedPlan(null); }} data-testid={`prepaid-${pp.sku}`}>
                        <span className="inline-block self-start text-xs px-3 py-1 rounded-full mb-3 font-medium bg-emerald-500/10 text-emerald-400">Pay In Full</span>
                        <h3 className="font-heading text-3xl font-bold text-white mb-2">{pp.name}</h3>
                        <div className="mb-1"><span className="font-heading text-5xl font-bold text-uf-red">£{pp.price}</span><span className="text-uf-textMuted"> total</span></div>
                        <div className="text-xs text-uf-textMuted mb-6">Equivalent £{(pp.price / pp.prepaid_months).toFixed(2)}/month · {pp.prepaid_months}-month fixed term</div>
                        <ul className="space-y-3 mb-8 flex-1">{(pp.features || []).map((f, i) => <li key={i} className="flex items-center gap-3 text-sm text-uf-textMuted"><Check size={16} className="text-uf-red flex-shrink-0" />{f}</li>)}</ul>
                        <button className={`w-full py-4 font-heading font-bold text-xl uppercase tracking-wider transition-colors mt-auto ${selectedPrepaid?.id === pp.id ? 'bg-uf-red text-white' : 'bg-transparent border border-uf-border text-white hover:border-uf-red'}`}>
                          {selectedPrepaid?.id === pp.id ? 'Selected' : 'Select Plan'}
                        </button>
                      </motion.div>
                    ))}
                  </div>
                  {selectedPrepaid && (
                    <div className="max-w-2xl mx-auto mt-8 bg-uf-surface border border-uf-border p-8">
                      <h3 className="font-heading text-2xl font-bold text-white mb-4">Your Details — {selectedPrepaid.name}</h3>
                      {error && <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 mb-4 text-sm" data-testid="prepaid-error">{error}</div>}
                      <div className="uf-form grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div><input value={prepaidForm.first_name} onChange={e => setPrepaidForm({ ...prepaidForm, first_name: e.target.value })} placeholder="First name" data-testid="prepaid-web-firstname" className="w-full px-4 py-3 bg-uf-bg border border-uf-border text-white" />{formErrors.first_name && <p className="text-red-400 text-xs mt-1">{formErrors.first_name}</p>}</div>
                        <div><input value={prepaidForm.last_name} onChange={e => setPrepaidForm({ ...prepaidForm, last_name: e.target.value })} placeholder="Last name" data-testid="prepaid-web-lastname" className="w-full px-4 py-3 bg-uf-bg border border-uf-border text-white" />{formErrors.last_name && <p className="text-red-400 text-xs mt-1">{formErrors.last_name}</p>}</div>
                        <div className="md:col-span-2"><input type="email" value={prepaidForm.email} onChange={e => setPrepaidForm({ ...prepaidForm, email: e.target.value })} placeholder="Email" data-testid="prepaid-web-email" className="w-full px-4 py-3 bg-uf-bg border border-uf-border text-white" />{formErrors.email && <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>}</div>
                        <div><label className="text-xs text-uf-textMuted block mb-1">Date of birth</label><input type="date" value={prepaidForm.date_of_birth} onChange={e => setPrepaidForm({ ...prepaidForm, date_of_birth: e.target.value })} data-testid="prepaid-web-dob" className="w-full px-4 py-3 bg-uf-bg border border-uf-border text-white" />{formErrors.date_of_birth && <p className="text-red-400 text-xs mt-1">{formErrors.date_of_birth}</p>}</div>
                      </div>
                      <div className="bg-uf-bg border border-uf-border p-4 mb-4 flex justify-between items-center">
                        <div>
                          <div className="text-white font-bold">{selectedPrepaid.prepaid_months}-month prepaid access</div>
                          <div className="text-uf-textMuted text-xs">Secure payment via Stripe. No Direct Debit. Fixed expiry date.</div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-heading font-bold text-uf-red">£{selectedPrepaid.price}</div>
                          <div className="text-xs text-uf-textMuted">one-off</div>
                        </div>
                      </div>
                      <button onClick={startPrepaidCheckout} disabled={prepaidLoading} data-testid="prepaid-web-checkout" className="btn-primary w-full disabled:opacity-50"><span>{prepaidLoading ? 'Redirecting to payment…' : `Pay £${selectedPrepaid.price} and Join`}</span></button>
                    </div>
                  )}
                </div>
              )}

              {!plansLoading && memberPlans.length > 0 && (
                <div className="mb-12">
                  <h2 className="font-heading text-2xl font-bold text-white mb-2 text-center">Gym Memberships at {cityOf(selectedSite)}</h2>
                  <p className="text-uf-textMuted text-center mb-6 text-sm">All prices below are accurate for this location.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {memberPlans.map((plan, index) => {
                      const isPrepaid = plan.plan_type === 'prepaid';
                      const isDayPass = plan.plan_type === 'day_pass';
                      const unitLabel = isPrepaid ? 'total' : isDayPass ? 'per visit' : '/month';
                      const features = plan.features && typeof plan.features === 'object' ? plan.features : {};
                      const bullets = Array.isArray(plan.features) ? plan.features : [];
                      const durationMonths = features.duration_months;
                      return (
                        <motion.div
                          key={plan.id}
                          className={`tier-card cursor-pointer flex flex-col ${selectedPlan?.id === plan.id ? 'border-uf-red' : ''}`}
                          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                          onClick={() => { setSelectedPlan(plan); setSelectedPrepaid(null); }}
                          data-testid={`tier-${plan.id}`}
                        >
                          {plan.price_source === 'site_override' && (
                            <span className="absolute top-4 right-4 text-[10px] bg-uf-red/10 text-uf-red border border-uf-red/40 px-2 py-0.5 uppercase tracking-wider">Local Price</span>
                          )}
                          <h3 className="font-heading text-2xl font-bold text-white mb-2">{plan.name}</h3>
                          <div className="mb-3">
                            <span className="font-heading text-5xl font-bold text-uf-red">£{Number(plan.price_monthly).toFixed(2)}</span>
                            <span className="text-uf-textMuted text-sm ml-1">{unitLabel}</span>
                          </div>
                          {durationMonths && !isDayPass && (
                            <div className="text-xs text-uf-textMuted mb-4">≈ £{(Number(plan.price_monthly) / durationMonths).toFixed(2)}/month · {durationMonths}-month term</div>
                          )}
                          {plan.description && <p className="text-uf-textMuted text-sm mb-4">{plan.description}</p>}
                          {bullets.length > 0 && (
                            <ul className="space-y-2 mb-6 flex-1">{bullets.map((f, i) => <li key={i} className="flex items-center gap-2 text-xs text-uf-textMuted"><Check size={14} className="text-uf-red flex-shrink-0" />{f}</li>)}</ul>
                          )}
                          <button className={`w-full py-3 font-heading font-bold uppercase tracking-wider transition-colors mt-auto ${selectedPlan?.id === plan.id ? 'bg-uf-red text-white' : 'bg-transparent border border-uf-border text-white hover:border-uf-red'}`}>
                            {selectedPlan?.id === plan.id ? 'Selected' : 'Select Plan'}
                          </button>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!plansLoading && ptPlans.length > 0 && (
                <div className="mb-12">
                  <h2 className="font-heading text-2xl font-bold text-white mb-2 text-center">Personal Trainer Memberships</h2>
                  <p className="text-uf-textMuted text-center mb-6 text-sm">Become a self-employed PT at UltraFlex</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                    {ptPlans.map((plan, index) => (
                      <motion.div key={plan.id} className={`tier-card cursor-pointer flex flex-col ${selectedPlan?.id === plan.id ? 'border-uf-red' : ''}`}
                        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}
                        onClick={() => { setSelectedPlan(plan); setSelectedPrepaid(null); }} data-testid={`tier-${plan.id}`}>
                        <span className={`inline-block self-start text-xs px-3 py-1 rounded-full mb-3 font-medium ${plan.plan_type === 'pt_monthly' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>
                          {plan.plan_type === 'pt_monthly' ? 'Monthly DD' : 'Ad-hoc (Card)'}
                        </span>
                        <h3 className="font-heading text-3xl font-bold text-white mb-2">{plan.name}</h3>
                        <div className="mb-6"><span className="font-heading text-5xl font-bold text-uf-red">£{Number(plan.price_monthly).toFixed(2)}</span><span className="text-uf-textMuted">/{plan.plan_type === 'pt_monthly' ? 'month' : 'session'}</span></div>
                        <ul className="space-y-3 mb-8 flex-1">{(Array.isArray(plan.features) ? plan.features : []).map((f, i) => <li key={i} className="flex items-center gap-3 text-sm text-uf-textMuted"><Check size={16} className="text-uf-red flex-shrink-0" />{f}</li>)}</ul>
                        <button className={`w-full py-4 font-heading font-bold text-xl uppercase tracking-wider transition-colors mt-auto ${selectedPlan?.id === plan.id ? 'bg-uf-red text-white' : 'bg-transparent border border-uf-border text-white hover:border-uf-red'}`}>
                          {selectedPlan?.id === plan.id ? 'Selected' : 'Select Plan'}
                        </button>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {selectedPlan && (
                <div className="text-center">
                  <button onClick={() => setStep(3)} className="btn-primary" data-testid="continue-btn"><span>Continue to Sign Up</span></button>
                </div>
              )}
            </motion.div>
          )}

          {/* STEP 3 — Your Details */}
          {step === 3 && (
            <motion.div className="max-w-2xl mx-auto bg-uf-surface border border-uf-border p-8 md:p-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <button onClick={() => setStep(2)} className="text-uf-textMuted hover:text-white mb-4 flex items-center gap-2" data-testid="back-to-plans-btn"><ArrowLeft size={14} />Back to plans</button>
              <h3 className="font-heading text-3xl font-bold text-white mb-2">Sign Up for {selectedPlan?.name}</h3>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="text-uf-textMuted">£{Number(selectedPlan?.price_monthly || 0).toFixed(2)}/{selectedPlan?.plan_type === 'pt_adhoc' ? 'session' : selectedPlan?.plan_type === 'prepaid' ? 'total' : selectedPlan?.plan_type === 'day_pass' ? 'per visit' : 'month'}</span>
                {selectedSite && (
                  <span className="inline-flex items-center gap-1 text-xs bg-uf-red/10 text-uf-red border border-uf-red/30 px-2 py-1" data-testid="details-gym-badge"><MapPin size={12} />{cityOf(selectedSite)}</span>
                )}
              </div>
              {error && <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 mb-6 text-sm">{error}</div>}
              <form onSubmit={handleDetailsSubmit} className="uf-form space-y-6" data-testid="signup-form" noValidate>
                <div>
                  <h4 className="text-white font-semibold mb-3">Personal Details</h4>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <input type="text" placeholder="First Name *" value={formData.first_name} onChange={e => { setFormData({...formData, first_name: e.target.value}); setFormErrors(p => ({...p, first_name: ''})); }} className={formErrors.first_name ? 'border-red-500' : ''} data-testid="input-first-name" />
                      {formErrors.first_name && <p className="text-red-400 text-xs mt-1">{formErrors.first_name}</p>}
                    </div>
                    <div>
                      <input type="text" placeholder="Last Name *" value={formData.last_name} onChange={e => { setFormData({...formData, last_name: e.target.value}); setFormErrors(p => ({...p, last_name: ''})); }} className={formErrors.last_name ? 'border-red-500' : ''} data-testid="input-last-name" />
                      {formErrors.last_name && <p className="text-red-400 text-xs mt-1">{formErrors.last_name}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <input type="email" placeholder="Email Address *" value={formData.email} onChange={e => { setFormData({...formData, email: e.target.value}); setFormErrors(p => ({...p, email: ''})); }} className={formErrors.email ? 'border-red-500' : ''} data-testid="input-email" />
                      {formErrors.email && <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>}
                    </div>
                    <div>
                      <input type="tel" placeholder="Phone Number *" value={formData.phone} onChange={e => { setFormData({...formData, phone: e.target.value}); setFormErrors(p => ({...p, phone: ''})); }} className={formErrors.phone ? 'border-red-500' : ''} data-testid="input-phone" />
                      {formErrors.phone && <p className="text-red-400 text-xs mt-1">{formErrors.phone}</p>}
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="text-uf-textMuted text-sm block mb-1">Date of Birth *</label>
                    <input type="date" value={formData.date_of_birth} onChange={e => { setFormData({...formData, date_of_birth: e.target.value}); setFormErrors(p => ({...p, date_of_birth: ''})); }} className={`w-full ${formErrors.date_of_birth ? 'border-red-500' : ''}`} data-testid="input-dob" />
                    {formErrors.date_of_birth && <p className="text-red-400 text-xs mt-1">{formErrors.date_of_birth}</p>}
                  </div>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-3">Address</h4>
                  <div className="mb-4">
                    <input type="text" placeholder="Address Line 1 *" value={formData.address_line1} onChange={e => { setFormData({...formData, address_line1: e.target.value}); setFormErrors(p => ({...p, address_line1: ''})); }} className={formErrors.address_line1 ? 'border-red-500' : ''} data-testid="input-address1" />
                    {formErrors.address_line1 && <p className="text-red-400 text-xs mt-1">{formErrors.address_line1}</p>}
                  </div>
                  <input type="text" placeholder="Address Line 2 (optional)" value={formData.address_line2} onChange={e => setFormData({...formData, address_line2: e.target.value})} className="mb-4" data-testid="input-address2" />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <input type="text" placeholder="City *" value={formData.city} onChange={e => { setFormData({...formData, city: e.target.value}); setFormErrors(p => ({...p, city: ''})); }} className={formErrors.city ? 'border-red-500' : ''} data-testid="input-city" />
                      {formErrors.city && <p className="text-red-400 text-xs mt-1">{formErrors.city}</p>}
                    </div>
                    <div>
                      <input type="text" placeholder="Postcode *" value={formData.postcode} onChange={e => { setFormData({...formData, postcode: e.target.value}); setFormErrors(p => ({...p, postcode: ''})); }} className={formErrors.postcode ? 'border-red-500' : ''} data-testid="input-postcode" />
                      {formErrors.postcode && <p className="text-red-400 text-xs mt-1">{formErrors.postcode}</p>}
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-3">Home Gym</h4>
                  <div className="bg-uf-bg border border-uf-border px-4 py-3 flex items-center justify-between" data-testid="details-gym-readonly">
                    <div className="flex items-center gap-3">
                      <MapPin size={16} className="text-uf-red" />
                      <span className="text-white font-heading uppercase tracking-wider text-sm">{cityOf(selectedSite) || 'Not selected'}</span>
                    </div>
                    <button type="button" onClick={() => setStep(1)} className="text-uf-red text-xs uppercase tracking-wider hover:underline" data-testid="change-gym-btn">Change</button>
                  </div>
                  {formErrors.home_site_id && <p className="text-red-400 text-xs mt-1">{formErrors.home_site_id}</p>}
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-3">Emergency Contact (Optional)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <input type="text" placeholder="Contact Name" value={formData.emergency_contact_name} onChange={e => setFormData({...formData, emergency_contact_name: e.target.value})} data-testid="input-emergency-name" />
                    <input type="tel" placeholder="Contact Phone" value={formData.emergency_contact_phone} onChange={e => setFormData({...formData, emergency_contact_phone: e.target.value})} data-testid="input-emergency-phone" />
                  </div>
                </div>
                {isPTPlan && (
                  <div>
                    <h4 className="text-white font-semibold mb-3">PT Business Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <input type="text" placeholder="Business/Trading Name" value={formData.business_name} onChange={e => setFormData({...formData, business_name: e.target.value})} data-testid="input-business-name" />
                      <input type="text" placeholder="UTR Number (Self-employed)" value={formData.self_employed_utr} onChange={e => setFormData({...formData, self_employed_utr: e.target.value})} data-testid="input-utr" />
                    </div>
                  </div>
                )}
                <div>
                  <h4 className="text-white font-semibold mb-3">Account Security</h4>
                  <input type="password" placeholder="Create Password (min 6 characters) *" value={formData.password} onChange={e => { setFormData({...formData, password: e.target.value}); setFormErrors(p => ({...p, password: ''})); }} className={formErrors.password ? 'border-red-500' : ''} minLength={6} data-testid="input-password" />
                  {formErrors.password && <p className="text-red-400 text-xs mt-1">{formErrors.password}</p>}
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={formData.marketing_opt_in} onChange={e => setFormData({...formData, marketing_opt_in: e.target.checked})} className="w-5 h-5 rounded border-uf-border text-uf-red focus:ring-uf-red" />
                  <span className="text-uf-textMuted text-sm">I'd like to receive news, offers and updates from UltraFlex</span>
                </label>
                <button type="submit" className="btn-primary w-full" data-testid="continue-to-payment"><span>Continue to Payment</span></button>
              </form>
            </motion.div>
          )}

          {/* STEP 4 — Direct Debit / Card Setup */}
          {step === 4 && (
            <motion.div className="max-w-2xl mx-auto bg-uf-surface border border-uf-border p-8 md:p-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <button onClick={() => setStep(3)} className="text-uf-textMuted hover:text-white mb-4 flex items-center gap-2" data-testid="back-to-details-btn"><ArrowLeft size={14} />Back to details</button>
              <h3 className="font-heading text-3xl font-bold text-white mb-2">{isAdHocPT ? 'Card Setup' : 'Direct Debit Setup'}</h3>
              <p className="text-uf-textMuted mb-6">{isAdHocPT ? 'Your card will be saved and charged each time you create a session access pass.' : 'Set up your Direct Debit to pay your monthly membership fee.'}</p>
              {proRata && !isAdHocPT && (
                <div className="bg-uf-red/5 border border-uf-red/20 rounded-xl p-5 mb-6" data-testid="pro-rata-breakdown">
                  <h4 className="text-white font-semibold mb-3">Payment Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-uf-textMuted"><span>Plan: {selectedPlan?.name}</span><span>£{proRata.monthly_amount?.toFixed(2)}/mo</span></div>
                    <div className="flex justify-between text-uf-textMuted"><span>Pro-rata ({proRata.days_remaining} of {proRata.days_in_month} days)</span><span>£{proRata.daily_rate}/day</span></div>
                    <div className="border-t border-uf-border pt-2 flex justify-between text-white font-semibold"><span>Due today (pro-rata)</span><span>£{proRata.pro_rata_amount?.toFixed(2)}</span></div>
                    <div className="flex justify-between text-uf-textMuted text-xs"><span>First full payment on</span><span>{proRata.first_full_payment_date}</span></div>
                  </div>
                </div>
              )}
              {error && <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 mb-6 text-sm">{error}</div>}
              <form onSubmit={handlePaymentSubmit} className="uf-form space-y-6" data-testid="payment-form">
                {isAdHocPT ? (
                  <div>
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
                      <p className="text-yellow-300 text-sm">Card details will be securely collected via Stripe after registration. You'll be prompted to add your card on your PT dashboard.</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h4 className="text-white font-semibold mb-3">Bank Account Details</h4>
                    <p className="text-uf-textMuted text-xs mb-4">Your Direct Debit will be set up under the Direct Debit Guarantee. You can cancel at any time.</p>
                    <input type="text" placeholder="Account Holder Name *" value={formData.account_holder_name} onChange={e => setFormData({...formData, account_holder_name: e.target.value})} required className="mb-4 w-full" data-testid="input-account-holder" />
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="text-uf-textMuted text-xs block mb-1">Sort Code *</label>
                        <input type="text" placeholder="00-00-00" maxLength={8} value={formData.sort_code} onChange={e => { let v = e.target.value.replace(/[^0-9-]/g, ''); setFormData({...formData, sort_code: v}); }} required data-testid="input-sort-code" />
                      </div>
                      <div>
                        <label className="text-uf-textMuted text-xs block mb-1">Account Number *</label>
                        <input type="text" placeholder="12345678" maxLength={8} value={formData.account_number} onChange={e => { let v = e.target.value.replace(/[^0-9]/g, ''); setFormData({...formData, account_number: v}); }} required data-testid="input-account-number" />
                      </div>
                    </div>
                    <input type="text" placeholder="Bank Name (optional)" value={formData.bank_name} onChange={e => setFormData({...formData, bank_name: e.target.value})} className="w-full" data-testid="input-bank-name" />
                    <div className="mt-6 bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                      <h5 className="text-white text-sm font-semibold mb-2">Direct Debit Guarantee</h5>
                      <ul className="text-uf-textMuted text-xs space-y-1.5">
                        <li>This Guarantee is offered by all banks and building societies that accept Direct Debits.</li>
                        <li>If there are any changes to the amount, date or frequency, UltraFlex will notify you 10 working days in advance.</li>
                        <li>If an error is made, you are entitled to a full and immediate refund from your bank.</li>
                        <li>You can cancel a Direct Debit at any time by contacting your bank.</li>
                      </ul>
                    </div>
                  </div>
                )}
                <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="submit-signup">
                  <span>{loading ? 'Setting up your account...' : isAdHocPT ? 'Complete Registration' : 'Set Up Direct Debit & Register'}</span>
                </button>
              </form>
            </motion.div>
          )}

          {/* STEP 5 — Agreement */}
          {step === 5 && memberId && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <MemberAgreementFlow memberId={memberId} memberName={`${formData.first_name} ${formData.last_name}`} onComplete={onAgreementComplete} isMigration={false} />
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
